import { Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

export interface SearchAutocompleteHandle {
  focus: () => void;
}

export type SearchOption<T> = {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  data?: T;
};

interface SearchAutocompleteProps<T> {
  options: SearchOption<T>[];
  placeholder?: string;
  emptyMessage?: string;
  value?: string;
  onValueChange?: (value: string, data?: T) => void;
  onInputChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  listClassName?: string;
  renderItem?: (
    option: SearchOption<T>,
    onSelect: (value: string) => void,
  ) => React.ReactNode;
  isLoading?: boolean;
}

function SearchAutocompleteInner<T>(
  {
    options,
    placeholder = "Buscar...",
    emptyMessage = "Nenhum resultado encontrado.",
    value,
    onValueChange,
    onInputChange,
    disabled = false,
    className,
    inputClassName,
    listClassName,
    renderItem,
    isLoading = false,
  }: SearchAutocompleteProps<T>,
  ref: React.Ref<SearchAutocompleteHandle>,
): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setOpen(newValue.length > 0);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (onInputChange) {
        onInputChange(newValue);
      }
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = useCallback(
    (currentValue: string) => {
      const selectedOption = options.find(
        (option) => option.value === currentValue,
      );

      if (selectedOption) {
        setInputValue(selectedOption.label);

        if (onValueChange) {
          onValueChange(currentValue, selectedOption.data);
        }
      }

      setOpen(false);
    },
    [options, onValueChange],
  );

  const clearInput = () => {
    setInputValue("");
    setOpen(false);

    if (onValueChange) {
      onValueChange("", undefined);
    }

    if (onInputChange) {
      onInputChange("");
    }
  };

  return (
    <div className={cn("relative w-full top-0 left-0 -mt-1", className)}>
      <div className="relative w-full">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          disabled={disabled}
          placeholder={placeholder}
          className={cn("w-full", inputClassName)}
          onFocus={() => inputValue && setOpen(true)}
          onBlur={() => {
            setTimeout(() => setOpen(false), 200);
          }}
        />
        {inputValue && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div
          className={cn(
            "fixed z-[9999] rounded-md border bg-popover shadow-md max-h-[300px] overflow-hidden",
            listClassName,
          )}
        >
          <Command>
            <CommandList>
              {isLoading ? (
                <CommandEmpty className="flex justify-center items-center h-10">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </CommandEmpty>
              ) : null}
              {options.length === 0 && !isLoading && (
                <CommandEmpty>{emptyMessage}</CommandEmpty>
              )}
              {options.length > 0 && !isLoading && (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={handleSelect}
                      className="py-3"
                    >
                      {renderItem ? (
                        renderItem(option, handleSelect)
                      ) : (
                        <div className="flex items-start gap-2">
                          {option.icon && (
                            <div className="mt-0.5 flex-shrink-0">
                              {option.icon}
                            </div>
                          )}
                          <div className="flex flex-col">
                            <span className="font-medium">{option.label}</span>
                            {option.description && (
                              <span className="text-sm text-muted-foreground">
                                {option.description}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}

export const SearchAutocomplete = forwardRef(SearchAutocompleteInner) as <T>(
  props: SearchAutocompleteProps<T> &
    React.RefAttributes<SearchAutocompleteHandle>,
) => React.ReactElement;
